import * as Sentry from "@sentry/react";
import type { AuthContext } from "@livingsyncs/types";
// Read INSIDE initSentry(), never at module scope — these consts resolve from
// the Capacitor/Electron bridge and a module-load read can freeze them
// undefined (see lib/platform.ts's own note on the bundling race).
import { isCapacitor, isElectron } from "./platform";

/**
 * US-126: drop the query string from a URL before it reaches Sentry.
 *
 * Resident/case/provider/medication name-search endpoints put free-text PHI in
 * the query string (e.g. `/api/residents?search=Jane%20Doe`), and Sentry's
 * default fetch/XHR breadcrumbs capture the full URL. We keep the path (useful
 * for debugging, no PHI) and replace the whole query with a marker.
 * HIPAA §164.312(b).
 */
export function scrubUrlQuery(url: unknown): unknown {
  if (typeof url !== "string") return url;
  const qIndex = url.indexOf("?");
  if (qIndex === -1) return url;
  return `${url.slice(0, qIndex)}?[redacted]`;
}

/**
 * US-011 follow-up: whether `environment` is a real deployed environment
 * that should get conservative (non-100%) sampling rates. VITE_SENTRY_ENVIRONMENT
 * is set to "prod"/"preprod" by CI (see ci.yml's `steps.ctx.outputs.name`) —
 * this previously checked `environment === "production"`, a string that never
 * matches, so every real deployment (prod AND preprod) silently sampled
 * traces/profiles/replays at 100% the whole time. Discovered via a live 429
 * (Too Many Requests) from Sentry's ingest endpoint on preprod session-replay's
 * initial flush — 100% replay capture in preprod burned through the project's
 * replay quota. Exported so this exact regression is unit-testable.
 */
export function isLiveEnvironment(environment: string): boolean {
  return environment === "prod" || environment === "preprod";
}

/**
 * Initialize Sentry for error tracking and performance monitoring
 * Frontend - Main authenticated application with RBAC context
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";

  // Never send errors to Sentry from local development — avoids noise and
  // prevents dev data from appearing in prod/preprod project dashboards.
  if (environment === "development") {
    return;
  }

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn("Sentry DSN not found. Error tracking is disabled.");
    return;
  }

  const liveEnvironment = isLiveEnvironment(environment);

  Sentry.init({
    dsn,
    environment,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Browser profiling for detailed performance insights
      Sentry.browserProfilingIntegration(),
      // Replay integration for session replay.
      // US-126: mask ALL rendered text. Session Replay records the DOM, and this
      // is a PHI-bearing app — resident names, DOB, diagnoses, medications and
      // care-plan text are rendered as plain text nodes, which `maskAllInputs`
      // alone does NOT cover. Default to masking everything (HIPAA §164.312(b));
      // opt specific non-sensitive UI back in with `data-sentry-unmask` if needed.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],

    // Performance Monitoring — see isLiveEnvironment() above for why this
    // isn't `environment === "production"`.
    tracesSampleRate: liveEnvironment ? 0.2 : 1.0, // 20% in prod/preprod, 100% otherwise
    profilesSampleRate: liveEnvironment ? 0.1 : 1.0, // 10% in prod/preprod

    // Session Replay
    replaysSessionSampleRate: liveEnvironment ? 0.1 : 1.0, // 10% in prod/preprod
    replaysOnErrorSampleRate: 1.0, // Always capture on error

    // Release tracking
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Which shell produced the event. The iPad build reports into the SAME
    // Sentry project as the web app (it IS the web app, wrapped in Capacitor),
    // so without this an iPad-only failure is indistinguishable from a browser
    // one. Both native shells shipped with no DSN at all until 2026-09-20, so
    // device errors — including the PIN flow's own — never reached Sentry.
    initialScope: {
      tags: { client: isCapacitor ? "ipad" : isElectron ? "desktop" : "web" },
    },

    // US-126: strip query strings from breadcrumb URLs as they are recorded
    // (fetch/xhr breadcrumbs store the request URL in `data.url`; navigation
    // breadcrumbs in `data.to`/`data.from`). This is where name-search PHI would
    // otherwise land.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        for (const key of ["url", "to", "from"] as const) {
          if (typeof breadcrumb.data[key] === "string") {
            breadcrumb.data[key] = scrubUrlQuery(breadcrumb.data[key]);
          }
        }
      }
      return breadcrumb;
    },

    // Additional options
    beforeSend(event) {
      // US-126: scrub the request URL's query string (can carry name-search PHI).
      if (event.request?.url) {
        event.request.url = scrubUrlQuery(event.request.url) as string;
      }
      if (event.request?.query_string) {
        // Never ship a raw query string map/string.
        event.request.query_string = "[redacted]";
      }

      // Mask sensitive data in breadcrumbs + scrub any URLs that slipped through.
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            // Mask sensitive fields
            const sensitiveFields = ["password", "token", "ssn", "credit_card"];
            sensitiveFields.forEach((field) => {
              if (breadcrumb.data?.[field]) {
                breadcrumb.data[field] = "[Filtered]";
              }
            });
            for (const key of ["url", "to", "from"] as const) {
              if (typeof breadcrumb.data[key] === "string") {
                breadcrumb.data[key] = scrubUrlQuery(breadcrumb.data[key]);
              }
            }
          }
          return breadcrumb;
        });
      }

      return event;
    },

    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Network errors (already handled by error boundaries)
      "NetworkError",
      "Network request failed",
      "Failed to fetch",
      // React Query expected errors
      "No active session",
      // Auth errors (these are expected during normal flow)
      "Invalid login credentials",
      "User not found",
    ],
  });
}

/**
 * Set auth context for Sentry error reporting
 * Call this whenever auth context changes
 */
export function setSentryAuthContext(authContext: AuthContext | null) {
  if (!authContext) {
    Sentry.setUser(null);
    Sentry.setContext("auth", null);
    return;
  }

  // Set user context (automatically included in all events)
  Sentry.setUser({
    id: authContext.userId,
    email: authContext.email,
    // Add custom user properties
    employeeId: authContext.employeeId || undefined,
  });

  // Set detailed auth context (for debugging RBAC issues)
  Sentry.setContext("auth", {
    roles: authContext.roles,
    isPlatformAdmin: authContext.isPlatformAdmin,
    isGroupHomeAdmin: authContext.isGroupHomeAdmin,
    isAuditorGroupAdmin: authContext.isAuditorGroupAdmin,
    facilityIds: authContext.facilityIds,
    groupHomeIds: authContext.groupHomeIds,
    auditorGroupIds: authContext.auditorGroupIds,
    // Role assumption info (important for debugging permission issues)
    isAssuming: authContext.isAssuming,
    actualRoles: authContext.actualRoles,
    assumedRole: authContext.assumedRole,
  });

  // Set facility context (important for facility-scoped operations)
  Sentry.setContext("facility", {
    defaultFacilityId: authContext.defaultFacilityId,
    facilityContextSource: authContext.facilityContextSource,
    isOnShift: authContext.isOnShift,
    currentShiftId: authContext.currentShift?.shiftId,
    currentShiftFacility: authContext.currentShift?.facilityId,
  });

  // Set tags for easier filtering in Sentry UI
  Sentry.setTag("role", authContext.roles[0] || "unknown");
  Sentry.setTag(
    "is_admin",
    authContext.isPlatformAdmin ||
      authContext.isPlatformManager ||
      authContext.isGroupHomeAdmin,
  );
  Sentry.setTag("is_assuming_role", authContext.isAssuming);
  Sentry.setTag("on_shift", authContext.isOnShift);
}

/**
 * Clear all Sentry context (call on logout)
 */
export function clearSentryContext() {
  Sentry.setUser(null);
  Sentry.setContext("auth", null);
  Sentry.setContext("facility", null);
}

/**
 * Manually capture an exception with context
 */
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
) {
  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
}

/**
 * Manually capture a message with context
 */
export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
) {
  Sentry.captureMessage(message, level);
}
